<template>
  <div id="app" :data-theme="theme">
    <header>
      <navbar></navbar>
    </header>
    <div id="wrapper">
      <div id="content" class="content-wrapper">
        <sidebar></sidebar>
        <navcrumb></navcrumb>
        <div id="main-content" tabindex="-1" class="main-content-target">
          <router-view v-if="ready"></router-view>
          <k-loading-component></k-loading-component>
          <k-screenshot></k-screenshot>
        </div>
      </div>
      <k-footer></k-footer>
    </div>
    <!-- Mounted before the modal is opened so the competency selector observes showModal
         transitioning to true and loads the programme's competencies (TK-3344). -->
    <add-edit-otj v-if="ready && !store.isDashboard"
      :show-modal="otjModalStore.isOtjModalOpen"
      @close="handleGlobalOtjModalClose"
      :programme-id="otjModalStore.otjModalData.programmeId"
      :is-editing="otjModalStore.otjModalData.isEditing"
      :otj-data="otjModalStore.otjModalData.otjData"
    ></add-edit-otj>
  </div>
</template>

<style>
.main-content-target:focus {
  outline: none;
}
</style>

<script>
import routes from '@kate-routes';
import useGlobalStore from '@stores/global';
import useFeatureFlagStore from '@stores/feature-flag';
import useThemeStore from '@stores/theme';
import useAppProgrammeStore from '@stores/app-programme-store';
import useOtjModalStore from '@stores/otj-modal-store';
import getOrNull from '@utils/get-or-null';
import getRoutesWithProperty from '@utils/get-routes-with-property';
import Navbar from './layout/navbar.vue';
import Navcrumb from './layout/navcrumb.vue';
import KScreenshot from './layout/k-screenshot.vue';
import Footer from './layout/footer.vue';
import Sidebar from './sidebar/sidebar.vue';
import AddEditOtj from './programmes/otj_components/add-edit-otj.vue';
import './styles';

const PUBLIC_PAGES = getRoutesWithProperty(routes, 'kate_is_public');
const FEATURE_FLAG_ROUTES = getRoutesWithProperty(routes, 'kate_feature_flag', true);
const FEATURE_FLAG_PAGES = FEATURE_FLAG_ROUTES.map(route => route.name);

export default {
  components: {
    Navbar,
    'k-footer': Footer,
    Navcrumb,
    KScreenshot,
    Sidebar,
    AddEditOtj,
  },

  data: () => ({
    ready: false,
    store: useGlobalStore(),
    themeStore: useThemeStore(),
    featureFlagStore: useFeatureFlagStore(),
    programmeStore: useAppProgrammeStore(),
    otjModalStore: useOtjModalStore(),
  }),

  created() {
    this.$logger.debug('Created root component');
    this.registerRouterCallbacks();
  },

  beforeMount() {
    this.$Loading.start();
    let startTime;
    // Safari doesn't have timeorigin, so set the start time as now
    // It'll be inaccurate to a few seconds, but best we can do for now
    if (window.performance.timeOrigin) {
      startTime = window.performance.timeOrigin / 1000;
    } else {
      startTime = new Date() / 1000;
    }
    this.$logger.debug('Initialised root component', {
      pageStartTime: startTime,
    });
    const kateStatus = this.checkKateStatus();
    const featureFlagClient = this.$featureFlags.getClient();
    Promise.all([kateStatus, featureFlagClient]).then(() => {
      this.$logger.info('Maintenance status checked and feature flag client initialised');
      this.getUserProfile().then(() => {
        const resolvedRoute = this.navigationGuard(this.$route);
        if (resolvedRoute) {
          // Navigation guard requires a redirect - wait for that to resolve before setting ready
          // to true and rendering the router-view to display the corresponding route component
          this.$router.push(resolvedRoute).then(() => {
            this.ready = true;
          });
        } else {
          // No redirect required - set ready to true rendering the router-view
          this.ready = true;
        }
      });
    });
  },

  computed: {
    theme() {
      return this.themeStore.theme;
    },
  },

  methods: {
    routerBeforeResolve(to, from) {
      this.$logger.resetRouteId();
      this.$logger.info(`Moving to page ${to.name}`, {
        from: from.fullPath,
        to: to.fullPath,
      }, true);
      if (from.path !== to.path) {
        // ignores any change in query params that are included in the fullPath
        this.$httpCancel();
      }
      // If resolvedRoute is defined, the nav guard says the user should be redirected to a different page
      const resolvedRoute = this.navigationGuard(to);
      // Returning undefined from router hook means the hook completed successfully and routing
      // will continue onto the next hook using the current route. If the hook returns a route object, the
      // routing will start again with the returned route
      return resolvedRoute;
    },
    routerAfterEach(to, from, failure) {
      if (from.path !== to.path) {
        this.$Loading.finish();
      }
      // Clear crumbs if we're routing to a new page and there was no navigation failure
      if (to.fullPath !== from.fullPath && !failure) {
        this.$crumbs.clear();
      }
      // Hook completed successfully - continue with routing and move to next hook
      // or if there was a failure, routing will stop here
      return undefined;
    },
    registerRouterCallbacks() {
      // Register the router beforeResolve and afterEach hooks
      this.$router.beforeResolve((to, from) => this.routerBeforeResolve(to, from));
      this.$router.afterEach((to, from, failure) => this.routerAfterEach(to, from, failure));
    },
    backupPageForMaintenance(to) {
      this.$cache.set('maintenance-prev-page', {
        name: to.name,
        params: to.params,
        query: to.query,
      }, 3600);
    },
    checkKateStatus() {
      return this.$http.get('/api/status').then(() => {}).catch(err => {
        if (getOrNull('response.data.err', err) === 'maintenance') {
          this.store.$patch({ inMaintenance: true });
          this.backupPageForMaintenance(this.$route);
          this.$router.push({ name: 'maintenance' });
        }
      });
    },
    getUserProfile() {
      this.$logger.info('Getting user profile info');
      return this.$http.get('/api/profile/user')
        .then(response => {
          this.$logger.info('Got user profile info');
          // Identify user in Flipt
          this.featureFlagStore.loadUser(response.data.id, response.data.email);
          this.store.logIn(response.data);
          const promises = [];
          if (this.store.appName === 'app') {
            promises.push(this.programmeStore.fetchProgrammes().catch(err => {
              this.$logger.error('Failed to fetch programmes', undefined, err);
              return null;
            }));
          }
          this.themeStore.resolveThemePreference(response.data);
          if (!this.store.termsAccepted && this.store.appName === 'app') {
            this.$logger.info('Redirecting to policy page', undefined, true);
            this.$router.push({ name: 'policy_accept_page' });
          }
          return Promise.all(promises).then(() => response.data);
        })
        .catch(err => {
          this.$logger.warn('Failed to get user profile info', undefined, err);
        });
    },
    navigationGuard(to) {
      // Check if KATE is in maintenance mode
      if (to.name !== 'maintenance' && this.store.inMaintenance) {
        this.backupPageForMaintenance(to);
        this.$logger.info('Redirecting to maintenance page', undefined, true);
        return { name: 'maintenance' };
      }
      // User must be logged in to access any page other than the public pages
      if (!this.store.isLoggedIn && PUBLIC_PAGES.indexOf(to.name) === -1) {
        this.$logger.info('Redirecting to login page', undefined, true);
        return { name: 'login_page', query: { next: encodeURIComponent(to.fullPath) } };
      }
      // On dashboard, user must have correct permissions to access the page
      if (this.store.isDashboard && !this.$permissions.hasDashboardPagePermission(to.name)) {
        this.$logger.info('User does not have required permissions - redirecting to 403', { route: to.name });
        return { name: '403' };
      }
      // Check if the route is protected by feature flag
      if (FEATURE_FLAG_PAGES.includes(to.name)) {
        this.$logger.info('Route is protected by feature flag - checking if user has access', { route: to.name });
        const destinationRoute = FEATURE_FLAG_ROUTES.find(route => route.name === to.name);
        if (!this.$featureFlags.isEnabled(destinationRoute.kate_feature_flag)) {
          this.$logger.info('User does not have required feature flag - redirecting to 404', { route: to.name });
          return { name: '404' };
        }
      }
      return undefined;
    },
    handleGlobalOtjModalClose() {
      this.otjModalStore.closeOtjModal();
    },
  },
};
</script>
